import { useGetUserFirstNameWithFallback, useGetUserLastNameWithFallback } from '@pba/react-utils';
import { type Event, type EventParticipant, isRegisteredEventParticipant } from 'models';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { Icon, type MenuProps } from 'ui';
import { useDirectMessageNode } from 'features/chat';
import { useGetEventRoleActions } from '../../../../../hooks';
import { useDeleteEventInvitee, useIsViewerEventInvitee } from '../../../../hooks';
import { useUpdateEventInviteeRole } from './useUpdateEventInviteeRole';

export const useEventInviteesActions = () => {
  const { t } = useTranslation();
  const isViewerEventInvitee = useIsViewerEventInvitee();
  const { canDeleteUserFromEvent, canEditUserRoleInEvent } = usePermissions();
  const { confirm: deleteEventInvitee, contextHolder, isLoadingDeletingRole } = useDeleteEventInvitee();
  const { isLoadingChangingRole, updateRole: updateEventInviteeRole } = useUpdateEventInviteeRole();
  const { handleDirectMessage } = useDirectMessageNode();
  const getRoleActions = useGetEventRoleActions();
  const getUserFirstNameWithFallback = useGetUserFirstNameWithFallback();
  const getUserLastNameWithFallback = useGetUserLastNameWithFallback();

  const getInviteesActions = useCallback(
    ({ currentInvitee, event }: { currentInvitee: EventParticipant; event: Event }) => {
      const actions: MenuProps['items'] = [];
      const { id, type } = event;
      const email = currentInvitee.email;

      if (isViewerEventInvitee(currentInvitee)) {
        return [];
      }

      if (canEditUserRoleInEvent({ event })) {
        const roleActions = getRoleActions({ eventType: type })
          .filter(({ role }) => currentInvitee.role !== role)
          .map(({ icon, key, label, role }) => ({
            icon,
            key,
            label: t(label),
            async onClick() {
              await updateEventInviteeRole({
                eventId: id,
                invitee: currentInvitee,
                role,
              });
            },
          }));

        actions.push(...roleActions);
      }

      if (isRegisteredEventParticipant(currentInvitee) && email) {
        actions.push({
          icon: <Icon icon="comment-line" />,
          key: 'message',
          label: t('Message'),
          async onClick() {
            await handleDirectMessage({
              _id: currentInvitee.ref,
              email,
              firstName: getUserFirstNameWithFallback(currentInvitee),
              lastName: getUserLastNameWithFallback(currentInvitee),
            });
          },
        });
      }

      if (canDeleteUserFromEvent({ event })) {
        actions.push({
          danger: true,
          icon: <Icon icon="minus-circle-line" />,
          key: 'remove',
          label: t('Remove from event'),
          onClick() {
            deleteEventInvitee({ eventId: id, invitee: currentInvitee });
          },
        });
      }

      return actions;
    },
    [
      canDeleteUserFromEvent,
      canEditUserRoleInEvent,
      deleteEventInvitee,
      getRoleActions,
      getUserFirstNameWithFallback,
      getUserLastNameWithFallback,
      handleDirectMessage,
      isViewerEventInvitee,
      t,
      updateEventInviteeRole,
    ],
  );

  return {
    contextHolder,
    getInviteesActions,
    isLoadingAction: isLoadingDeletingRole || isLoadingChangingRole,
  };
};
